import React, { useEffect, useRef, useState } from 'react';

const sanitizeTrackingValue = (value, maxLength = 300) => {
  if (typeof value !== 'string') {
    return '';
  }

  return value
    .split('')
    .filter((character) => {
      const code = character.charCodeAt(0);
      return code >= 32 && code !== 127;
    })
    .join('')
    .trim()
    .slice(0, maxLength);
};

const sanitizePromoCode = (value) =>
  sanitizeTrackingValue(value, 40)
    .replace(/[^a-zA-Z0-9_-]/g, '')
    .toUpperCase();

const getSessionStorageItem = (key) => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const removeSessionStorageItem = (key) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.removeItem(key);
  } catch (error) {
    // Ignore storage failures so inquiry form behavior remains functional.
  }
};

// Contact form submits via FormSubmit AJAX for inline success/error states.
export const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const statusRef = useRef(null);
  const [serviceType, setServiceType] = useState('');
  const [preferredContactMethod, setPreferredContactMethod] = useState('email');
  const [smsConsent, setSmsConsent] = useState(false);
  const smsConsentRef = useRef(null);
  const [promoCode, setPromoCode] = useState('');
  const [trackingFields, setTrackingFields] = useState({
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_term: '',
    utm_content: '',
    utm_id: '',
    utm_source_platform: '',
    utm_creative_format: '',
    utm_marketing_tactic: '',
    gclid: '',
    gbraid: '',
    wbraid: '',
    fbclid: '',
    msclkid: '',
    ttclid: '',
    landing_page: '',
    referrer: '',
    first_landing_page: '',
    first_referrer: ''
  });
  const serviceTypeRef = useRef(null);
  const phoneInputRef = useRef(null);
  const isPhoneRequired = preferredContactMethod === 'text' || preferredContactMethod === 'call';
  const phonePattern = '^(?=(?:\\D*\\d){10,})[\\d()+.\\-\\s]+$';
  const serviceTypeLabels = {
    equine: 'Equine Photography',
    'black-background': 'Black Background Equine',
    wedding: 'Wedding Photography',
    couples: 'Couples Session',
    family: 'Family Photography',
    senior: 'Senior Portraits',
    prom: 'Prom Photography',
    pets: 'Pet Photography',
    portrait: 'Portrait Session',
    'horse-show-private-client': 'Horse Show Private Client'
  };
  const subjectServiceLabel = serviceTypeLabels[serviceType] || 'Photography';
  const submissionSubject = `New ${subjectServiceLabel} inquiry from website`;
  const getLocalDateInputMin = () => {
    const now = new Date();
    const timezoneOffsetMs = now.getTimezoneOffset() * 60 * 1000;
    return new Date(now.getTime() - timezoneOffsetMs).toISOString().split('T')[0];
  };

  const handlePhoneInvalid = (event) => {
    if (isPhoneRequired && !event.target.value) {
      event.target.setCustomValidity('Please add a phone number so Cassidy can call or text you back.');
      return;
    }

    if (event.target.validity.patternMismatch) {
      event.target.setCustomValidity('Please enter a valid phone number with at least 10 digits.');
      return;
    }

    event.target.setCustomValidity('Please enter a valid phone number.');
  };

  const clearPhoneCustomValidity = (event) => {
    event.target.setCustomValidity('');
  };

  const validatePreferredSessionDate = (dateInput) => {
    if (!dateInput) {
      return true;
    }

    dateInput.setCustomValidity('');

    if (!dateInput.value) {
      return true;
    }

    const selectedDate = new Date(`${dateInput.value}T00:00:00`);
    const minimumDateValue = getLocalDateInputMin();
    const minimumDate = new Date(`${minimumDateValue}T00:00:00`);

    if (selectedDate < minimumDate) {
      dateInput.setCustomValidity('Please choose today or a future date for your preferred session date.');
      dateInput.reportValidity();
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (phoneInputRef.current && !isPhoneRequired) {
      phoneInputRef.current.setCustomValidity('');
    }
  }, [isPhoneRequired]);

  useEffect(() => {
    if (!['text', 'call'].includes(preferredContactMethod)) {
      setSmsConsent(false);
    }
  }, [preferredContactMethod]);

  useEffect(() => {
    if (!status.message || !statusRef.current) {
      return;
    }

    statusRef.current.focus();
  }, [status]);

  useEffect(() => {
    const mapSessionToServiceType = {
      equine: 'equine',
      'black-background': 'black-background',
      blackbackground: 'black-background',
      wedding: 'wedding',
      weddings: 'wedding',
      couples: 'couples',
      family: 'family',
      senior: 'senior',
      seniors: 'senior',
      prom: 'prom',
      pet: 'pets',
      pets: 'pets',
      portrait: 'portrait',
      portraits: 'portrait',
      'horse-show-private-client': 'horse-show-private-client',
      horseshowprivateclient: 'horse-show-private-client',
      'horse-show-private': 'horse-show-private-client',
      horseshowprivate: 'horse-show-private-client'
    };

    const mapQueryToServiceType = {
      equine: 'equine',
      'equine-photography': 'equine',
      'black-background': 'black-background',
      blackbackground: 'black-background',
      blackbg: 'black-background',
      'black-background-equine': 'black-background',
      'black-background-horse': 'black-background',
      'black-background-horses': 'black-background',
      wedding: 'wedding',
      weddings: 'wedding',
      'wedding-photography': 'wedding',
      'wedding-session': 'wedding',
      couples: 'couples',
      couple: 'couples',
      engagement: 'couples',
      'couples-session': 'couples',
      family: 'family',
      families: 'family',
      'family-photography': 'family',
      senior: 'senior',
      seniors: 'senior',
      'senior-portraits': 'senior',
      prom: 'prom',
      'prom-photography': 'prom',
      pet: 'pets',
      pets: 'pets',
      'pet-photography': 'pets',
      portrait: 'portrait',
      portraits: 'portrait',
      'portrait-session': 'portrait',
      'horse-show-private-client': 'horse-show-private-client',
      'horse-show-private-clients': 'horse-show-private-client',
      'horse-show-private': 'horse-show-private-client',
      'horse-show': 'horse-show-private-client',
      horseshow: 'horse-show-private-client',
      'horse-show-client': 'horse-show-private-client'
    };

    const normalizeParam = (value) =>
      typeof value === 'string'
        ? value
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9\s_-]/g, '')
            .replace(/[\s_]+/g, '-')
        : '';

    const applyHeroSelection = () => {
      const preselected = getSessionStorageItem('hero_selected_session_type');

      if (preselected && mapSessionToServiceType[preselected]) {
        setServiceType(mapSessionToServiceType[preselected]);
        removeSessionStorageItem('hero_selected_session_type');
      }

      const shouldFocusContact = getSessionStorageItem('hero_focus_contact_form') === '1';

      if (shouldFocusContact && typeof window !== 'undefined') {
        removeSessionStorageItem('hero_focus_contact_form');
        window.requestAnimationFrame(() => {
          if (serviceTypeRef.current) {
            serviceTypeRef.current.focus();
          }
        });
      }
    };

    applyHeroSelection();

    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const serviceTypeParam =
        params.get('service_type') ||
        params.get('service') ||
        params.get('session_type') ||
        params.get('session');
      const normalizedServiceType = normalizeParam(serviceTypeParam);
      const mappedServiceType = mapQueryToServiceType[normalizedServiceType];

      if (mappedServiceType) {
        setServiceType((currentValue) => currentValue || mappedServiceType);
      }
      const preferredContactParam = normalizeParam(
        params.get('preferred_contact_method') || params.get('contact_method') || params.get('contact')
      );

      if (['email', 'text', 'call'].includes(preferredContactParam)) {
        setPreferredContactMethod(preferredContactParam);
      }

      const promoCodeParam =
        params.get('promo_code') ||
        params.get('promo') ||
        params.get('code') ||
        params.get('campaign_code');

      if (promoCodeParam) {
        const sanitizedPromoCode = sanitizePromoCode(promoCodeParam);
        if (sanitizedPromoCode) {
          setPromoCode((currentValue) => currentValue || sanitizedPromoCode);
        }
      }

      const storageKey = 'csp_tracking_fields_v1';
      let storedTrackingFieldsRaw = null;

      try {
        storedTrackingFieldsRaw = window.localStorage.getItem(storageKey);
      } catch (error) {
        storedTrackingFieldsRaw = null;
      }

      let storedTrackingFields = null;
      if (storedTrackingFieldsRaw) {
        try {
          storedTrackingFields = JSON.parse(storedTrackingFieldsRaw);
        } catch (error) {
          try {
            window.localStorage.removeItem(storageKey);
          } catch (storageError) {
            // Ignore storage cleanup failures (private mode, disabled storage, etc.)
          }
        }
      }

      const existingFirstLandingPage = storedTrackingFields?.first_landing_page || storedTrackingFields?.landing_page || '';
      const existingFirstReferrer = storedTrackingFields?.first_referrer || storedTrackingFields?.referrer || '';

      const nextTrackingFields = {
        utm_source: sanitizeTrackingValue(params.get('utm_source') || storedTrackingFields?.utm_source || '', 120),
        utm_medium: sanitizeTrackingValue(params.get('utm_medium') || storedTrackingFields?.utm_medium || '', 120),
        utm_campaign: sanitizeTrackingValue(params.get('utm_campaign') || storedTrackingFields?.utm_campaign || '', 160),
        utm_term: sanitizeTrackingValue(params.get('utm_term') || storedTrackingFields?.utm_term || '', 160),
        utm_content: sanitizeTrackingValue(params.get('utm_content') || storedTrackingFields?.utm_content || '', 160),
        utm_id: sanitizeTrackingValue(params.get('utm_id') || storedTrackingFields?.utm_id || '', 120),
        utm_source_platform: sanitizeTrackingValue(
          params.get('utm_source_platform') || storedTrackingFields?.utm_source_platform || '',
          120
        ),
        utm_creative_format: sanitizeTrackingValue(
          params.get('utm_creative_format') || storedTrackingFields?.utm_creative_format || '',
          120
        ),
        utm_marketing_tactic: sanitizeTrackingValue(
          params.get('utm_marketing_tactic') || storedTrackingFields?.utm_marketing_tactic || '',
          120
        ),
        gclid: sanitizeTrackingValue(params.get('gclid') || storedTrackingFields?.gclid || '', 160),
        gbraid: sanitizeTrackingValue(params.get('gbraid') || storedTrackingFields?.gbraid || '', 160),
        wbraid: sanitizeTrackingValue(params.get('wbraid') || storedTrackingFields?.wbraid || '', 160),
        fbclid: sanitizeTrackingValue(params.get('fbclid') || storedTrackingFields?.fbclid || '', 160),
        msclkid: sanitizeTrackingValue(params.get('msclkid') || storedTrackingFields?.msclkid || '', 160),
        ttclid: sanitizeTrackingValue(params.get('ttclid') || storedTrackingFields?.ttclid || '', 160),
        landing_page: sanitizeTrackingValue(window.location.href, 400),
        referrer: sanitizeTrackingValue(document.referrer || storedTrackingFields?.referrer || 'direct', 400),
        first_landing_page: sanitizeTrackingValue(existingFirstLandingPage || window.location.href, 400),
        first_referrer: sanitizeTrackingValue(existingFirstReferrer || document.referrer || 'direct', 400)
      };

      setTrackingFields(nextTrackingFields);

      try {
        window.localStorage.setItem(storageKey, JSON.stringify(nextTrackingFields));
      } catch (error) {
        // Ignore storage failures so inquiry form behavior remains functional.
      }
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('hero:availability-click', applyHeroSelection);
      window.addEventListener('hashchange', applyHeroSelection);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('hero:availability-click', applyHeroSelection);
        window.removeEventListener('hashchange', applyHeroSelection);
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }

    const form = e.currentTarget;
    const preferredDateInput = form.elements.namedItem('preferred_session_date');

    if (!validatePreferredSessionDate(preferredDateInput)) {
      return;
    }

    setStatus({ type: '', message: '' });

    const requiredTextFields = [
      { name: 'first_name', label: 'First name' },
      { name: 'last_name', label: 'Last name' },
      { name: 'email', label: 'Email address' },
      {
        name: 'message',
        label: 'Message',
        minLength: 20,
        tooShortMessage: 'Please share a few more details (at least 20 characters) so Cassidy can help.'
      }
    ];

    const firstInvalidField = requiredTextFields.find(({ name, minLength }) => {
      const field = form.elements.namedItem(name);
      const value = field && typeof field.value === 'string' ? field.value.trim() : '';

      if (!field || !value) {
        return true;
      }

      return typeof minLength === 'number' && value.length < minLength;
    });

    if (firstInvalidField) {
      const invalidField = form.elements.namedItem(firstInvalidField.name);

      if (invalidField && typeof invalidField.focus === 'function') {
        invalidField.focus();
      }

      setStatus({
        type: 'error',
        message:
          firstInvalidField.tooShortMessage || `${firstInvalidField.label} can't be blank.`
      });
      return;
    }

    if (['text', 'call'].includes(preferredContactMethod) && !smsConsent) {
      if (smsConsentRef.current && typeof smsConsentRef.current.focus === 'function') {
        smsConsentRef.current.focus();
      }

      setStatus({
        type: 'error',
        message: 'Please check the consent box to receive text/call updates about your inquiry.'
      });
      return;
    }

    setIsSubmitting(true);

    const formData = new FormData(form);

    requiredTextFields.forEach(({ name }) => {
      const value = formData.get(name);
      if (typeof value === 'string') {
        const normalizedValue = name === 'email' ? value.trim().toLowerCase() : value.trim();
        formData.set(name, normalizedValue);
      }
    });

    const normalizedEmail = formData.get('email');
    if (typeof normalizedEmail === 'string' && normalizedEmail) {
      formData.set('_replyto', normalizedEmail);
    }

    const sanitizedPromoCode = sanitizePromoCode(formData.get('promo_code'));
    formData.set('promo_code', sanitizedPromoCode);

    const visitorTimezone =
      typeof Intl !== 'undefined' && typeof Intl.DateTimeFormat === 'function'
        ? Intl.DateTimeFormat().resolvedOptions().timeZone || ''
        : '';
    formData.set('submitted_at_utc', new Date().toISOString());
    formData.set('visitor_timezone', sanitizeTrackingValue(visitorTimezone, 80));

    const abortController = new AbortController();
    const timeoutId = window.setTimeout(() => abortController.abort(), 12000);

    try {
      const response = await fetch('https://formsubmit.co/ajax/cassidy@cassidysullivanphotography.com', {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        },
        body: formData,
        signal: abortController.signal
      });

      if (!response.ok) {
        throw new Error('Form submission failed');
      }

      const payload = await response.json();
      const submissionSucceeded = payload?.success === true || payload?.success === 'true';

      if (!submissionSucceeded) {
        throw new Error(payload?.message || 'Form submission failed');
      }

      form.reset();
      setServiceType('');
      setPreferredContactMethod('email');
      setSmsConsent(false);
      setPromoCode('');
      setStatus({
        type: 'success',
        message: "Thanks! Your message was sent. Cassidy will respond within 24 hours."
      });
    } catch (error) {
      const timedOut = error?.name === 'AbortError';

      setStatus({
        type: 'error',
        message: timedOut
          ? 'The request timed out. Please try again or email cassidy@cassidysullivanphotography.com directly.'
          : 'Something went wrong sending your message. Please email cassidy@cassidysullivanphotography.com directly.'
      });
    } finally {
      window.clearTimeout(timeoutId);
      setIsSubmitting(false);
    }
  };

  return React.createElement(
    'div',
    {
      id: 'contact-form',
      className: 'bg-white/20 backdrop-blur-md rounded-2xl p-8 border border-white/30 scroll-mt-28'
    },
    React.createElement(
      'h3',
      {
        className: 'text-2xl font-bold mb-6'
      },
      'Get in Touch'
    ),
    React.createElement(
      'form',
      {
        className: 'space-y-6',
        onSubmit: handleSubmit
      },
      // FormSubmit metadata + spam controls
      React.createElement('input', { type: 'hidden', name: '_subject', value: submissionSubject }),
      React.createElement('input', {
        type: 'hidden',
        name: 'promo_campaign',
        value: 'STANDARD_INQUIRY'
      }),
      React.createElement('input', { type: 'hidden', name: '_template', value: 'table' }),
      React.createElement('input', { type: 'hidden', name: '_captcha', value: 'true' }),
      React.createElement('input', { type: 'hidden', name: 'utm_source', value: trackingFields.utm_source }),
      React.createElement('input', { type: 'hidden', name: 'utm_medium', value: trackingFields.utm_medium }),
      React.createElement('input', { type: 'hidden', name: 'utm_campaign', value: trackingFields.utm_campaign }),
      React.createElement('input', { type: 'hidden', name: 'utm_term', value: trackingFields.utm_term }),
      React.createElement('input', { type: 'hidden', name: 'utm_content', value: trackingFields.utm_content }),
      React.createElement('input', { type: 'hidden', name: 'utm_id', value: trackingFields.utm_id }),
      React.createElement('input', {
        type: 'hidden',
        name: 'utm_source_platform',
        value: trackingFields.utm_source_platform
      }),
      React.createElement('input', {
        type: 'hidden',
        name: 'utm_creative_format',
        value: trackingFields.utm_creative_format
      }),
      React.createElement('input', {
        type: 'hidden',
        name: 'utm_marketing_tactic',
        value: trackingFields.utm_marketing_tactic
      }),
      React.createElement('input', { type: 'hidden', name: 'gclid', value: trackingFields.gclid }),
      React.createElement('input', { type: 'hidden', name: 'gbraid', value: trackingFields.gbraid }),
      React.createElement('input', { type: 'hidden', name: 'wbraid', value: trackingFields.wbraid }),
      React.createElement('input', { type: 'hidden', name: 'fbclid', value: trackingFields.fbclid }),
      React.createElement('input', { type: 'hidden', name: 'msclkid', value: trackingFields.msclkid }),
      React.createElement('input', { type: 'hidden', name: 'ttclid', value: trackingFields.ttclid }),
      React.createElement('input', { type: 'hidden', name: 'landing_page', value: trackingFields.landing_page }),
      React.createElement('input', { type: 'hidden', name: 'referrer', value: trackingFields.referrer }),
      React.createElement('input', { type: 'hidden', name: 'first_landing_page', value: trackingFields.first_landing_page }),
      React.createElement('input', { type: 'hidden', name: 'first_referrer', value: trackingFields.first_referrer }),
      React.createElement('input', {
        type: 'hidden',
        name: 'sms_call_opt_in',
        value: ['text', 'call'].includes(preferredContactMethod) && smsConsent ? 'yes' : 'no'
      }),
      React.createElement('input', {
        type: 'text',
        name: '_honey',
        tabIndex: '-1',
        autoComplete: 'new-password',
        'aria-hidden': 'true',
        style: {
          position: 'absolute',
          left: '-5000px'
        }
      }),

      status.message &&
        React.createElement(
          'div',
          {
            className:
              status.type === 'success'
                ? 'rounded-lg border border-emerald-400/40 bg-emerald-500/20 px-4 py-3 text-emerald-100'
                : 'rounded-lg border border-red-400/40 bg-red-500/20 px-4 py-3 text-red-100',
            role: status.type === 'success' ? 'status' : 'alert',
            'aria-live': status.type === 'success' ? 'polite' : 'assertive',
            'aria-atomic': 'true',
            tabIndex: -1,
            ref: statusRef
          },
          status.message
        ),

      React.createElement(
        'div',
        {
          className: 'grid grid-cols-1 md:grid-cols-2 gap-4'
        },
        React.createElement(
          'div',
          { className: 'space-y-2' },
          React.createElement(
            'label',
            {
              htmlFor: 'first_name',
              className: 'sr-only'
            },
            'First name'
          ),
          React.createElement('input', {
            id: 'first_name',
            type: 'text',
            name: 'first_name',
            placeholder: 'First Name',
            autoComplete: 'given-name',
            autoCapitalize: 'words',
            maxLength: 80,
            required: true,
            className:
              'w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-300 focus:outline-none focus:border-teal-400'
          })
        ),
        React.createElement(
          'div',
          { className: 'space-y-2' },
          React.createElement(
            'label',
            {
              htmlFor: 'last_name',
              className: 'sr-only'
            },
            'Last name'
          ),
          React.createElement('input', {
            id: 'last_name',
            type: 'text',
            name: 'last_name',
            placeholder: 'Last Name',
            autoComplete: 'family-name',
            autoCapitalize: 'words',
            maxLength: 80,
            required: true,
            className:
              'w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-300 focus:outline-none focus:border-teal-400'
          })
        )
      ),

      React.createElement(
        'div',
        { className: 'space-y-2' },
        React.createElement(
          'label',
          {
            htmlFor: 'email',
            className: 'sr-only'
          },
          'Email address'
        ),
        React.createElement('input', {
          id: 'email',
          type: 'email',
          name: 'email',
          placeholder: 'Email Address',
          autoComplete: 'email',
          autoCapitalize: 'none',
          autoCorrect: 'off',
          spellCheck: false,
          maxLength: 254,
          required: true,
          className:
            'w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-300 focus:outline-none focus:border-teal-400'
        })
      ),

      React.createElement(
        'div',
        { className: 'space-y-2' },
        React.createElement(
          'label',
          {
            htmlFor: 'phone',
            className: 'sr-only'
          },
          'Phone number'
        ),
        React.createElement('input', {
          id: 'phone',
          type: 'tel',
          name: 'phone',
          placeholder: isPhoneRequired ? 'Phone Number (required for text/call)' : 'Phone Number (optional)',
          'aria-describedby': 'phone-help',
          autoComplete: 'tel-national',
          inputMode: 'tel',
          pattern: phonePattern,
          title: 'Please enter a valid phone number using at least 10 digits.',
          required: isPhoneRequired,
          ref: phoneInputRef,
          onInvalid: handlePhoneInvalid,
          onInput: clearPhoneCustomValidity,
          className:
            'w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-300 focus:outline-none focus:border-teal-400'
        })
      ),
      React.createElement(
        'p',
        {
          id: 'phone-help',
          className: 'text-xs text-gray-300 -mt-3'
        },
        isPhoneRequired
          ? 'Phone number is required when text or call is selected as your preferred contact method.'
          : 'Phone number is optional unless you prefer text or call responses.'
      ),

      React.createElement(
        'div',
        {
          className: 'space-y-2'
        },
        React.createElement(
          'label',
          {
            htmlFor: 'preferred_session_date',
            className: 'block text-sm font-medium text-cyan-100'
          },
          'Preferred Session Date (optional)'
        ),
        React.createElement(
          'p',
          {
            className: 'text-xs text-gray-300'
          },
          'Tell us your ideal shoot date so Cassidy can check availability faster.'
        ),
        React.createElement(
          'div',
          {
            className: 'grid grid-cols-1 md:grid-cols-2 gap-4'
          },
          React.createElement('input', {
            id: 'preferred_session_date',
            type: 'date',
            name: 'preferred_session_date',
            min: getLocalDateInputMin(),
            'aria-label': 'Preferred session date',
            onFocus: (event) => event.target.setAttribute('min', getLocalDateInputMin()),
            onInput: (event) => event.target.setCustomValidity(''),
            onBlur: (event) => {
              validatePreferredSessionDate(event.target);
            },
            className:
              'w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:border-teal-400'
          }),
          React.createElement(
            'div',
            {
              className: 'space-y-2'
            },
            React.createElement(
              'label',
              {
                htmlFor: 'preferred_contact_method',
                className: 'block text-sm font-medium text-cyan-100'
              },
              'Preferred Contact Method'
            ),
            React.createElement(
              'select',
              {
                id: 'preferred_contact_method',
                name: 'preferred_contact_method',
                'aria-label': 'Preferred contact method',
                'aria-describedby': ['text', 'call'].includes(preferredContactMethod)
                  ? 'sms-call-disclosure'
                  : undefined,
                required: true,
                value: preferredContactMethod,
                onChange: (e) => setPreferredContactMethod(e.target.value),
                className:
                  'w-full px-4 py-3 rounded-lg bg-gray-800 border border-white/20 text-white focus:outline-none focus:border-teal-400 [&>option]:bg-gray-800 [&>option]:text-white'
              },
              React.createElement('option', { value: '' }, 'Select contact method'),
              React.createElement('option', { value: 'email' }, 'Email'),
              React.createElement('option', { value: 'text' }, 'Text'),
              React.createElement('option', { value: 'call' }, 'Call')
            ),
            ['text', 'call'].includes(preferredContactMethod) &&
              React.createElement(
                React.Fragment,
                null,
                React.createElement(
                  'p',
                  {
                    id: 'sms-call-disclosure',
                    className: 'text-xs text-gray-300'
                  },
                  'By choosing text or call, you consent to Cassidy contacting you at the number provided. Message/data rates may apply.'
                ),
                React.createElement(
                  'label',
                  {
                    htmlFor: 'sms-call-consent',
                    className: 'mt-2 flex items-start gap-2 text-xs text-gray-200'
                  },
                  React.createElement('input', {
                    id: 'sms-call-consent',
                    type: 'checkbox',
                    name: 'sms_call_consent_checkbox',
                    checked: smsConsent,
                    onChange: (event) => setSmsConsent(event.target.checked),
                    required: true,
                    ref: smsConsentRef,
                    className: 'mt-0.5 h-4 w-4 rounded border-white/30 bg-white/10 accent-teal-400'
                  }),
                  React.createElement(
                    'span',
                    null,
                    'I agree to receive text/call updates about this inquiry at the phone number provided.'
                  )
                )
              )
          )
        )
      ),

      React.createElement(
        'div',
        {
          className: 'space-y-2'
        },
        React.createElement(
          'label',
          {
            htmlFor: 'service_type',
            className: 'block text-sm font-medium text-cyan-100'
          },
          'Session Type'
        ),
        React.createElement(
          'select',
          {
            id: 'service_type',
            name: 'service_type',
            'aria-label': 'Service type',
            required: true,
            ref: serviceTypeRef,
            value: serviceType,
            onChange: (e) => setServiceType(e.target.value),
            className:
              'w-full px-4 py-3 rounded-lg bg-gray-800 border border-white/20 text-white focus:outline-none focus:border-teal-400 [&>option]:bg-gray-800 [&>option]:text-white'
          },
          React.createElement('option', { value: '', className: 'bg-gray-800 text-white' }, 'Select Session Type'),
          React.createElement('option', { value: 'equine', className: 'bg-gray-800 text-white' }, 'Equine Photography'),
          React.createElement('option', { value: 'black-background', className: 'bg-gray-800 text-white' }, 'Black Background Equine'),
          React.createElement('option', { value: 'wedding', className: 'bg-gray-800 text-white' }, 'Wedding Photography'),
          React.createElement('option', { value: 'couples', className: 'bg-gray-800 text-white' }, 'Couples Session'),
          React.createElement('option', { value: 'family', className: 'bg-gray-800 text-white' }, 'Family Photography'),
          React.createElement('option', { value: 'senior', className: 'bg-gray-800 text-white' }, 'Senior Portraits'),
          React.createElement('option', { value: 'prom', className: 'bg-gray-800 text-white' }, 'Prom Photography'),
          React.createElement('option', { value: 'pets', className: 'bg-gray-800 text-white' }, 'Pet Photography'),
          React.createElement('option', { value: 'portrait', className: 'bg-gray-800 text-white' }, 'Portrait Session'),
          React.createElement('option', { value: 'horse-show-private-client', className: 'bg-gray-800 text-white' }, 'Horse Show Private Client')
        )
      ),

      React.createElement(
        'div',
        { className: 'space-y-2' },
        React.createElement(
          'label',
          {
            htmlFor: 'promo_code',
            className: 'sr-only'
          },
          'Promo code'
        ),
        React.createElement('input', {
          id: 'promo_code',
          type: 'text',
          name: 'promo_code',
          placeholder: 'Promo Code (optional)',
          maxLength: 40,
          value: promoCode,
          onChange: (event) => setPromoCode(sanitizePromoCode(event.target.value)),
          className:
            'w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-300 focus:outline-none focus:border-teal-400'
        })
      ),

      React.createElement(
        'div',
        { className: 'space-y-2' },
        React.createElement(
          'label',
          {
            htmlFor: 'message',
            className: 'sr-only'
          },
          'Tell me about your vision'
        ),
        React.createElement('textarea', {
          id: 'message',
          placeholder: 'Tell me about your vision...',
          name: 'message',
          rows: '4',
          minLength: 20,
          maxLength: 2000,
          required: true,
          className:
            'w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-300 focus:outline-none focus:border-teal-400 resize-none'
        })
      ),

      React.createElement(
        'label',
        {
          htmlFor: 'contact-consent',
          className: 'flex items-start gap-3 text-sm text-gray-200'
        },
        React.createElement('input', {
          id: 'contact-consent',
          type: 'checkbox',
          name: 'contact_consent',
          value: 'yes',
          required: true,
          className: 'mt-1 h-4 w-4 rounded border-white/30 bg-white/10 accent-teal-400'
        }),
        React.createElement(
          'span',
          null,
          'I consent to being contacted by Cassidy Sullivan Photography about this inquiry.'
        )
      ),

      React.createElement(
        'button',
        {
          type: 'submit',
          disabled: isSubmitting,
          className:
            'w-full bg-gradient-to-r from-teal-500 to-cyan-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-teal-600 hover:to-cyan-700 transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100'
        },
        isSubmitting ? 'Sending...' : 'Send Message'
      )
    )
  );
};
